import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  centredView,
  clampView,
  coverScale,
  MAX_ZOOM,
  OUTPUT,
  sourceSquare,
  VIEW,
  withView,
  zoomAt,
  type Loaded,
  type Size,
  type View,
} from './photo-crop';

async function renderCrop(image: HTMLImageElement, size: Size, view: View): Promise<Blob> {
  const source = sourceSquare(size, view);
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT;
  canvas.height = OUTPUT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not prepare the photo');
  // JPEG has no transparency; white is kinder than black behind a transparent PNG.
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, OUTPUT, OUTPUT);
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, source.x, source.y, source.side, source.side, 0, 0, OUTPUT, OUTPUT);
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not prepare the photo'))), 'image/jpeg', 0.9)
  );
}

type Props = {
  /** Object URL of the chosen file; null keeps the dialog closed. */
  imageUrl: string | null;
  saving: boolean;
  onCancel: () => void;
  onSave: (photo: Blob) => void;
};

/**
 * Pan and zoom a chosen photo inside a circle before it is saved. Drag or use the arrow keys to
 * move it; the slider, the buttons or the mouse wheel zoom.
 */
export function PhotoCropDialog({ imageUrl, saving, onCancel, onSave }: Props) {
  const imageRef = useRef<HTMLImageElement>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ x: number; y: number } | null>(null);
  // Tagged with the URL it is for, so a new photo starts afresh without an effect.
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const current = loaded && loaded.url === imageUrl ? loaded : null;

  const setView = (update: (size: Size, view: View) => View) => setLoaded(withView(update));

  // Wheel zoom needs a non-passive listener so the page doesn't scroll as well.
  const hasImage = current != null;
  useEffect(() => {
    const area = areaRef.current;
    if (!area || !hasImage) return undefined;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = area.getBoundingClientRect();
      const px = event.clientX - rect.left;
      const py = event.clientY - rect.top;
      setLoaded(withView((size, view) => zoomAt(size, view, view.zoom * Math.exp(-event.deltaY / 500), px, py)));
    };
    area.addEventListener('wheel', onWheel, { passive: false });
    return () => area.removeEventListener('wheel', onWheel);
  }, [hasImage]);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { x: event.clientX, y: event.clientY };
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const last = dragRef.current;
    if (!last) return;
    const dx = event.clientX - last.x;
    const dy = event.clientY - last.y;
    dragRef.current = { x: event.clientX, y: event.clientY };
    setView((size, view) => clampView(size, { ...view, x: view.x + dx, y: view.y + dy }));
  };
  const endDrag = () => {
    dragRef.current = null;
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [8, 0],
      ArrowRight: [-8, 0],
      ArrowUp: [0, 8],
      ArrowDown: [0, -8],
    };
    const move = moves[event.key];
    if (move) {
      event.preventDefault();
      setView((size, view) => clampView(size, { ...view, x: view.x + move[0], y: view.y + move[1] }));
    } else if (event.key === '+' || event.key === '=' || event.key === '-') {
      event.preventDefault();
      const factor = event.key === '-' ? 1 / 1.1 : 1.1;
      setView((size, view) => zoomAt(size, view, view.zoom * factor));
    }
  };

  const save = async () => {
    const image = imageRef.current;
    if (!image || !current) return;
    try {
      onSave(await renderCrop(image, current.size, current.view));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const view = current?.view;
  const scale = current ? coverScale(current.size) * current.view.zoom : 1;

  return (
    <Dialog open={imageUrl != null} onOpenChange={(open) => (!open && !saving ? onCancel() : undefined)}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Position photo</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4">
          <div
            ref={areaRef}
            role="application"
            tabIndex={0}
            aria-label="Photo position. Drag or use the arrow keys to move it; plus and minus zoom."
            className="relative cursor-grab touch-none overflow-hidden rounded-md bg-muted select-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none active:cursor-grabbing"
            style={{ width: VIEW, height: VIEW }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onKeyDown={onKeyDown}
          >
            {imageUrl ? (
              <img
                ref={imageRef}
                src={imageUrl}
                alt=""
                draggable={false}
                className="pointer-events-none absolute top-0 left-0 max-w-none origin-top-left"
                style={
                  current && view
                    ? {
                        width: current.size.w * scale,
                        height: current.size.h * scale,
                        transform: `translate(${view.x}px, ${view.y}px)`,
                      }
                    : { visibility: 'hidden' }
                }
                onLoad={(event) => {
                  const img = event.currentTarget;
                  const size = { w: img.naturalWidth, h: img.naturalHeight };
                  setError(null);
                  setLoaded({ url: imageUrl, size, view: centredView(size) });
                }}
                onError={() => setError('That file could not be opened as an image')}
              />
            ) : null}
            {/* Dims the corners so the circle shows what avatars will show. */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-full"
              style={{ boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)' }}
            />
          </div>
          <div className="flex w-full max-w-64 items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon-compact"
              aria-label="Zoom out"
              disabled={!current || view!.zoom <= 1}
              onClick={() => setView((size, v) => zoomAt(size, v, v.zoom / 1.25))}
            >
              <Minus />
            </Button>
            <input
              type="range"
              min={1}
              max={MAX_ZOOM}
              step={0.01}
              value={view?.zoom ?? 1}
              disabled={!current}
              aria-label="Zoom"
              className="h-1.5 flex-1 cursor-pointer accent-primary"
              onChange={(event) => {
                const zoom = Number(event.target.value);
                setView((size, v) => zoomAt(size, v, zoom));
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-compact"
              aria-label="Zoom in"
              disabled={!current || view!.zoom >= MAX_ZOOM}
              onClick={() => setView((size, v) => zoomAt(size, v, v.zoom * 1.25))}
            >
              <Plus />
            </Button>
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button loading={saving} loadingText="Saving…" type="button" onClick={() => void save()} disabled={!current}>
            Save photo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
